import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import Column from "@components/column";
import Loader from "@components/loader";
import DashboardCard from "@commons/cards/dashboard";
import Row from "@components/row";
import { RootState } from "@store/index";
import { Module, Dashboard as DashboardApi, Zone } from "@apis/index";
import useConfig from "@hooks/useConfig";
import OrderStatusCards from "@commons/orders/OrderStatusCards";
import OverviewCard from "@commons/cards/overview";

const Dashboard = () => {
  const { bucketUrl } = useConfig();
  const { data: modules } = useSelector((state: RootState) => state.modules);
  const { data: zones } = useSelector((state: RootState) => state.zones);
  const { data: topPerformersData } = useSelector(
    (state: RootState) => state.topPerformers,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedModuleIdParam = searchParams.get("moduleId");
  const selectedModuleType = searchParams.get("module")?.toLowerCase();

  // Find the selected module object dynamically
  const selectedModule =
    modules?.find((mod: any) => {
      if (selectedModuleIdParam) {
        return String(mod.id) === selectedModuleIdParam;
      }
      return mod.name.toLowerCase() === selectedModuleType;
    }) ||
    (() => {
      const savedModule = localStorage.getItem("selectedModule");
      const savedModuleId = localStorage.getItem("selectedModuleId");
      if (savedModule && savedModuleId) {
        const found = modules?.find(
          (mod: any) => String(mod.id) === savedModuleId,
        );
        if (found) return found;
      }
      return modules?.[0];
    })();

  const [zoneId, setZoneId] = useState<number | string>("all");

  // Unified Date Filter State
  const [dateFilter, setDateFilter] = useState("All Time");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [apiDates, setApiDates] = useState({ startDate: "", endDate: "" });

  const filterOptions = [
    "All Time",
    "Today",
    "This Week",
    "This Month",
    "This Year",
    "Custom Time",
  ];

  // Build the "View All" link to the dedicated Top Performers page, preserving
  // the currently selected module/zone context.
  const buildTopPerformersLink = (
    type: "product" | "deliveryman" | "customer",
  ) => {
    const params = new URLSearchParams();
    params.set("type", type);
    if (selectedModule?.name) params.set("module", selectedModule.name);
    if (selectedModule?.id) params.set("moduleId", String(selectedModule.id));
    if (zoneId && zoneId !== "all") params.set("zoneId", String(zoneId));
    return `/admin/dashboard/top-performers?${params.toString()}`;
  };

  const fetchModules = async () => {
    try {
      await Module.list({ state: "modules" });
    } catch (error) {
      console.error("Failed to fetch modules", error);
    }
  };

  const fetchZones = async () => {
    try {
      await Zone.list({ state: "zones" });
    } catch (error) {
      console.error("Failed to fetch zones", error);
    }
  };

  const fetchTopPerformers = async () => {
    console.log("[Dashboard] fetchTopPerformers starting", {
      selectedModuleId: selectedModule?.id,
      selectedModuleName: selectedModule?.name,
      zoneId,
    });
    try {
      await DashboardApi.topPerformers({
        state: "topPerformers",
        params: {
          limit: 7,
          ...(selectedModule?.id ? { moduleId: selectedModule.id } : {}),
          ...(zoneId !== "all" ? { zoneId } : {}),
        },
      });
    } catch (error) {
      console.error("Failed to fetch top performers", error);
    }
  };

  useEffect(() => {
    fetchModules();
    fetchZones();
  }, []);

  // Redirect and save module selection context
  useEffect(() => {
    if (!modules || modules.length === 0) return;

    const paramModule = searchParams.get("module");
    const paramModuleId = searchParams.get("moduleId");

    if (paramModule && paramModuleId) {
      localStorage.setItem("selectedModule", paramModule);
      localStorage.setItem("selectedModuleId", paramModuleId);
    } else {
      const savedModule = localStorage.getItem("selectedModule");
      const savedModuleId = localStorage.getItem("selectedModuleId");

      let targetModule = modules[0];
      if (savedModule && savedModuleId) {
        const found = modules.find(
          (mod: any) => String(mod.id) === savedModuleId,
        );
        if (found) {
          targetModule = found;
        }
      }

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("module", targetModule.name);
          next.set("moduleId", String(targetModule.id));
          return next;
        },
        { replace: true },
      );
    }
  }, [modules, searchParams, setSearchParams]);

  console.log("[Dashboard] Render state:", {
    selectedModuleType,
    modulesCount: modules?.length,
    selectedModuleId: selectedModule?.id,
    selectedModuleName: selectedModule?.name,
  });

  useEffect(() => {
    if (!modules || modules.length === 0) {
      console.log("[Dashboard] useEffect skipped - modules not loaded yet");
      return;
    }
    console.log(
      "[Dashboard] useEffect triggered for selectedModule?.id or zoneId",
      {
        selectedModuleId: selectedModule?.id,
        zoneId,
      },
    );
    fetchTopPerformers();
  }, [selectedModule?.id, zoneId, modules]);

  // Update dates for API calls
  useEffect(() => {
    const today = new Date();
    let startDate = "";
    let endDate = "";

    if (dateFilter === "Today") {
      startDate = today.toISOString().split("T")[0];
      endDate = today.toISOString().split("T")[0];
    } else if (dateFilter === "This Week") {
      const first = today.getDate() - today.getDay();
      const last = first + 6;
      const firstDay = new Date(today.getFullYear(), today.getMonth(), first);
      const lastDay = new Date(today.getFullYear(), today.getMonth(), last);
      startDate = firstDay.toISOString().split("T")[0];
      endDate = lastDay.toISOString().split("T")[0];
    } else if (dateFilter === "This Month") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      startDate = firstDay.toISOString().split("T")[0];
      endDate = lastDay.toISOString().split("T")[0];
    } else if (dateFilter === "This Year") {
      const firstDay = new Date(today.getFullYear(), 0, 1);
      const lastDay = new Date(today.getFullYear(), 11, 31);
      startDate = firstDay.toISOString().split("T")[0];
      endDate = lastDay.toISOString().split("T")[0];
    } else if (dateFilter === "Custom Time") {
      startDate = customStartDate;
      endDate = customEndDate;
    }

    if (dateFilter !== "Custom Time" || (startDate && endDate)) {
      setApiDates({ startDate, endDate });
    }
  }, [dateFilter, customStartDate, customEndDate]);

  if (!modules || modules.length === 0) {
    return <Loader fullScreen />;
  }

  // Default card config
  const defaultCardConfig = [
    {
      key: "items",
      title: "Items",
      icon: "/svgs/default/items.svg",
      isUp: true,
    },
    {
      key: "orders",
      title: "Orders",
      icon: "/svgs/default/orders.svg",
      isUp: true,
    },
    {
      key: "stores",
      title: "Stores",
      icon: "/svgs/default/stores.svg",
      isUp: false,
    },
    {
      key: "customers",
      title: "Customers",
      icon: "/svgs/customers.svg",
      isUp: false,
    },
  ];

  // Dynamic card config based on module type (keeping existing logic)
  const cardConfig = (() => {
    switch (selectedModule?.type) {
      case "food":
        return [
          {
            key: "items",
            title: "Foods",
            icon: "/svgs/food/food.svg",
            isUp: true,
            link: "/admin/product-management/products",
          },
          {
            key: "orders",
            title: "Orders",
            icon: "/svgs/food/orders.svg",
            isUp: true,
            link: "/admin/orders/all",
          },
          {
            key: "stores",
            title: "Restaurants",
            icon: "/svgs/food/stores.svg",
            isUp: false,
            link: "/admin/users/store/store-lists",
          },
          {
            key: "customers",
            title: "Customers",
            icon: "/svgs/customers.svg",
            isUp: false,
            link: "/admin/users/customer/customer-lists",
          },
        ];
      case "grocery":
        return [
          {
            key: "items",
            title: "Items",
            icon: "/svgs/grocery/items.svg",
            isUp: true,
            link: "/admin/product-management/products",
          },
          {
            key: "orders",
            title: "Orders",
            icon: "/svgs/grocery/orders.svg",
            isUp: true,
            link: "/admin/orders/all",
          },
          {
            key: "stores",
            title: "Grocery Stores",
            icon: "/svgs/grocery/stores.svg",
            isUp: false,
            link: "/admin/users/store/store-lists",
          },
          {
            key: "customers",
            title: "Customers",
            icon: "/svgs/customers.svg",
            isUp: false,
            link: "/admin/users/customer/customer-lists",
          },
        ];
      case "store":
        return [
          {
            key: "items",
            title: "Products",
            icon: "/svgs/store/products.svg",
            isUp: true,
            link: "/admin/product-management/products",
          },
          {
            key: "orders",
            title: "Orders",
            icon: "/svgs/store/orders.svg",
            isUp: true,
            link: "/admin/orders/all",
          },
          {
            key: "stores",
            title: "Stores",
            icon: "/svgs/store/stores.svg",
            isUp: false,
            link: "/admin/users/store/store-lists",
          },
          {
            key: "customers",
            title: "Customers",
            icon: "/svgs/customers.svg",
            isUp: false,
            link: "/admin/users/customer/customer-lists",
          },
        ];
      case "pharma":
        return [
          {
            key: "items",
            title: "Items",
            icon: "/svgs/pharma/pharmacy.svg",
            isUp: true,
            link: "/admin/product-management/products",
          },
          {
            key: "orders",
            title: "Orders",
            icon: "/svgs/pharma/orders.svg",
            isUp: true,
            link: "/admin/orders/all",
          },
          {
            key: "stores",
            title: "Stores",
            icon: "/svgs/pharma/stores.svg",
            isUp: false,
            link: "/admin/users/store/store-lists",
          },
          {
            key: "customers",
            title: "Customers",
            icon: "/svgs/customers.svg",
            isUp: false,
            link: "/admin/users/customer/customer-lists",
          },
        ];
      default:
        return defaultCardConfig.map((card) => ({
          ...card,
          link:
            card.title === "Orders" ? "/admin/orders/all" : "/admin/dashboard",
        }));
    }
  })();

  const titleConfig = {
    title: selectedModule?.name || "Unknown Module",
    icon: selectedModule?.icon
      ? `${bucketUrl}/${selectedModule?.icon}`
      : selectedModule?.name === "Any Services"
        ? "/svgs/anyservicetitle.svg"
        : `/svgs/${selectedModule?.name?.toLowerCase()}title.svg`,
  };

  return (
    <Fragment>
      <Column>
        <DashboardCard
          cardConfig={cardConfig}
          titleConfig={titleConfig}
          zoneId={zoneId}
          setZoneId={setZoneId}
          moduleId={selectedModule?.id}
          startDate={apiDates.startDate}
          endDate={apiDates.endDate}
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          customStartDate={customStartDate}
          setCustomStartDate={setCustomStartDate}
          customEndDate={customEndDate}
          setCustomEndDate={setCustomEndDate}
          filterOptions={filterOptions}
        />
        <OrderStatusCards
          storeId={0}
          moduleId={selectedModule?.id}
          zoneId={zoneId === "all" ? undefined : zoneId}
          startDate={apiDates.startDate}
          endDate={apiDates.endDate}
        />

        <Row>
          <OverviewCard
            title="Top Selling Items"
            data={topPerformersData?.topSellingItems}
            type="product"
            link={buildTopPerformersLink("product")}
          />
          <OverviewCard
            title="Top Deliveryman"
            data={topPerformersData?.topDeliverymen}
            type="deliveryman"
            link={buildTopPerformersLink("deliveryman")}
          />
          <OverviewCard
            title="Top Customer"
            data={topPerformersData?.topCustomers}
            type="customer"
            link={buildTopPerformersLink("customer")}
          />
        </Row>
      </Column>
    </Fragment>
  );
};

export default Dashboard;
export const countConfig = [
  {
    icon: "/counticons/unassigned.svg",
    title: "Unassigned Orders",
    count: "18",
    status: "unassigned",
  },
  {
    icon: "/counticons/accepted.svg",
    title: "Accepted By Delivery Man",
    count: "0",
    status: "accepted",
  },
  {
    icon: "/counticons/packaging.svg",
    title: "Packaging",
    count: "6",
    status: "packaging",
  },
  {
    icon: "/counticons/out-for.svg",
    title: "Out For Delivery",
    count: "0",
    status: "delivering",
  },
  {
    icon: "/counticons/delivered.svg",
    title: "Delivered",
    count: "15",
    status: "delivered",
  },
  {
    icon: "/counticons/canceled.svg",
    title: "Cancelled",
    count: "1",
    status: "cancelled",
  },
  {
    icon: "/counticons/refunded.svg",
    title: "Refunded",
    count: "1",
    status: "refunded",
  },
  {
    icon: "/counticons/payment-failed.svg",
    title: "Failed",
    count: "1",
    status: "failed",
  },
];
